import React, { useState, useEffect, useCallback } from 'react';
import { apiClient, errorMessage } from '../api';
import { cardClasses, secondaryButton, tableHeadCell } from '../components/ui';
import { outdatedCount } from '../lib/outdated';
import { formatDate, formatDateTime } from '../lib/dates';

const gb = (bytes) => bytes == null ? '-' : `${(bytes / 1e9).toFixed(1)} GB`;
const mb = (bytes) => bytes == null ? '-' : `${(bytes / 1e6).toFixed(1)} MB`;
const when = (iso) => iso ? formatDateTime(iso) : '-';
const STALE_DAYS = 365;

const Tile = ({ label, value, hint, tone = 'text-slate-900' }) => (
    <div className="p-4 bg-white border border-slate-900/10 rounded-xl shadow-sm">
        <p className={`text-2xl font-semibold ${tone}`}>{value}</p>
        <p className="text-sm text-slate-600">{label}</p>
        {hint && <p className="mt-1 text-xs text-slate-400 break-words">{hint}</p>}
    </div>
);

const Th = ({ children }) => <th className={tableHeadCell}>{children}</th>;

// Por qué no coinciden los updates registrados con los zips de la carpeta. Son dos casos y sólo uno se arregla sincronizando.
const zipNotes = (v) => [
    v.unregistered_zips > 0 && `${v.unregistered_zips} ${v.unregistered_zips === 1 ? 'zip sin registrar: se registra' : 'zips sin registrar: se registran'} al sincronizar${v.active === false ? ', una vez reactivada' : ''}`,
    v.missing_zips > 0 && `${v.missing_zips} ${v.missing_zips === 1 ? 'update registrado' : 'updates registrados'} sin zip en test/: sincronizar no lo corrige (están en el informe de integridad)`,
].filter(Boolean);

// Estado del servidor: disco, última sincronización, versiones y carpetas de test que la app no sincroniza.
export default function StatusSection({ addToast }) {
    const [status, setStatus] = useState(null);
    const load = useCallback(async () => {
        try { setStatus((await apiClient.get('/api/status')).data); }
        catch (e) { addToast(errorMessage(e, 'No se pudo cargar el estado.'), 'error'); }
    }, [addToast]);
    useEffect(() => { load(); }, [load]);
    if (!status) return <p className="text-sm text-slate-500">Cargando estado…</p>;

    const usedPct = status.disk ? Math.round(status.disk.used / status.disk.total * 100) : null;
    const stray = status.versions.filter(v => v.not_zip.length > 0);
    const isStale = (iso) => (Date.now() - new Date(iso).getTime()) / 86400000 > STALE_DAYS;
    const staleCount = status.work_branches.filter(b => isStale(b.modified)).length;
    const duplicates = { files: 0, publications: 0, missing_indexes: [], ...status.duplicates };
    const repeated = duplicates.files + duplicates.publications;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg tracking-tight text-slate-900">Estado del servidor</h3>
                <button type="button" onClick={load} className={secondaryButton}>Actualizar</button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Tile label="Espacio libre en disco" value={gb(status.disk?.free)} hint={usedPct != null ? `${usedPct}% usado de ${gb(status.disk.total)}` : status.base_dir} tone={usedPct > 90 ? 'text-red-600' : undefined} />
                <Tile label="Base de datos" value={mb(status.database_bytes)} hint={`Symlinks: ${status.symlink_mode === 'relative' ? 'relativos' : 'absolutos'}`} />
                <Tile label="Última sincronización" value={status.sync.running ? 'En curso…' : (status.sync.last_at ? formatDate(status.sync.last_at) : 'Nunca')} hint={status.sync.last_at ? `${when(status.sync.last_at)} · ${status.sync.last_by}` : undefined} />
                <Tile label="Descargas registradas" value={status.downloads.tracked} tone={status.downloads.readable ? undefined : 'text-amber-700'}
                    hint={status.downloads.readable ? `Leído hasta ${when(status.downloads.read_until)}` : `No se puede leer ${status.downloads.log}`} />
            </div>

            {(repeated > 0 || duplicates.missing_indexes.length > 0) && (
                <p role="alert" className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                    {repeated > 0 && <>La base tiene registros repetidos sin unificar: {duplicates.files} de zips y {duplicates.publications} de publicaciones. </>}
                    {duplicates.missing_indexes.length > 0 && <>Falta el índice único <span className="font-mono">{duplicates.missing_indexes.join(', ')}</span>: la base no está protegida contra duplicados. </>}
                    Se corrige con «Unificar duplicados», en Sincronización e integridad.
                </p>
            )}

            {status.sync.last_result && <p className="p-3 rounded-lg bg-white border border-slate-900/10 text-sm text-slate-700"><span className="font-semibold">Resultado: </span>{status.sync.last_result}</p>}

            <div className={cardClasses}>
                <h4 className="font-semibold text-slate-800 mb-3">Versiones</h4>
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50"><tr><Th>Versión</Th><Th>En la base</Th><Th>Zips en test/</Th><Th>Publicaciones</Th><Th>Desactualizados</Th><Th>Observaciones</Th></tr></thead>
                    <tbody>
                        {status.versions.map(v => (
                            <tr key={v.name} className="border-b border-slate-100">
                                <td className="px-3 py-1.5 font-mono font-medium whitespace-nowrap">{v.name}{v.active === false && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 font-sans text-xs font-normal text-slate-500">desactivada</span>}</td>
                                <td className="px-3 py-1.5">{v.files}</td>
                                <td className={`px-3 py-1.5 ${zipNotes(v).length > 0 ? 'font-semibold text-amber-700' : ''}`}>{v.on_disk ? v.zips_on_disk : '-'}</td>
                                <td className="px-3 py-1.5">{v.published}</td>
                                <td className={`px-3 py-1.5 whitespace-nowrap ${v.outdated_files > 0 ? 'font-semibold text-red-600' : 'text-slate-400'}`}>{outdatedCount(v.outdated_files, v.outdated_clients)}</td>
                                <td className="px-3 py-1.5 text-xs text-slate-500">
                                    {zipNotes(v).map(note => <span key={note} className="block font-semibold text-amber-700">{note}</span>)}
                                    {[v.active === false && 'no se sincroniza ni se ofrece', !v.on_disk && 'no existe en test/', v.not_zip.length > 0 && `${v.not_zip.length} archivo(s) que no son zip`].filter(Boolean).join(' · ')}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {stray.length > 0 && (
                    <details className="mt-3 text-sm">
                        <summary className="cursor-pointer font-semibold text-amber-700">Archivos que no son zip dentro de las carpetas de updates</summary>
                        <ul className="mt-2 font-mono text-xs text-slate-600">{stray.flatMap(v => v.not_zip.map(n => <li key={v.name + n}>test/{v.name}/Updates/{n}</li>))}</ul>
                    </details>
                )}
            </div>

            <div className={cardClasses}>
                <h4 className="font-semibold text-slate-800">Ramas de trabajo en test/ <span className="font-normal text-slate-500">· {status.work_branches.length}</span></h4>
                <p className="text-sm text-slate-500 mb-3">Carpetas que no son versiones: la app no las sincroniza. {staleCount > 0 && <span className="font-semibold text-amber-700">{staleCount} sin cambios hace más de un año.</span>}</p>
                <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0"><tr><Th>Carpeta</Th><Th>Zips</Th><Th>Última modificación</Th></tr></thead>
                        <tbody>
                            {status.work_branches.map(b => (
                                <tr key={b.name} className="border-b border-slate-100">
                                    <td className="px-3 py-1.5 font-mono">{b.name}</td>
                                    <td className="px-3 py-1.5">{b.zips}</td>
                                    <td className={`px-3 py-1.5 ${isStale(b.modified) ? 'text-amber-700' : 'text-slate-600'}`}>{formatDate(b.modified)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
